import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Eye, Pencil, Send, FileText, MoreVertical, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { contractsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { ContractStatus, ContractFilters } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const statusColors: Record<ContractStatus, string> = {
  DRAFT: 'secondary',
  PENDING_SIGNATURE: 'warning',
  PARTIALLY_SIGNED: 'warning',
  SIGNED: 'success',
  EXPIRED: 'destructive',
  CANCELLED: 'destructive',
}

const statusLabels: Record<ContractStatus, string> = {
  DRAFT: 'Draft',
  PENDING_SIGNATURE: 'Pending Signature',
  PARTIALLY_SIGNED: 'Partially Signed',
  SIGNED: 'Signed',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
}

export default function ContractsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [filters, setFilters] = useState<ContractFilters>({})

  const { data: contractsData, isLoading } = useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => contractsApi.getAll(filters),
  })
  const contracts = contractsData ?? []

  const deleteMutation = useMutation({
    mutationFn: contractsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Contract deleted successfully')
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete contract'))
    },
  })

  const cancelMutation = useMutation({
    mutationFn: contractsApi.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Contract cancelled')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to cancel contract'))
    },
  })

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatCurrency = (value?: number, currency = 'USD') => {
    if (value === undefined || value === null) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Contracts</h1>
          <p className="text-muted-foreground">
            Manage your contracts and track signing progress
          </p>
        </div>
        {canAddEdit('CONTRACTS') && (
          <Button onClick={() => navigate('/contracts/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Contract
          </Button>
        )}
      </div>

      <div className="flex gap-4">
        <Input
          placeholder="Search contracts..."
          className="max-w-sm"
          value={filters.search || ''}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <Select
          value={filters.status || 'ALL'}
          onValueChange={(v) => setFilters({ ...filters, status: v === 'ALL' ? undefined : v as ContractStatus })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PENDING_SIGNATURE">Pending Signature</SelectItem>
            <SelectItem value="PARTIALLY_SIGNED">Partially Signed</SelectItem>
            <SelectItem value="SIGNED">Signed</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No contracts yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first contract to get started with e-signing.
            </p>
            {canAddEdit('CONTRACTS') && (
              <Button onClick={() => navigate('/contracts/new')}>
                <Plus className="mr-2 h-4 w-4" />
                Create Contract
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contract #</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signers</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow key={contract.contract_id}>
                  <TableCell className="font-mono text-sm">{contract.contract_number}</TableCell>
                  <TableCell className="font-medium">{contract.title}</TableCell>
                  <TableCell className="text-muted-foreground">{contract.client_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={statusColors[contract.status] as any}>
                      {statusLabels[contract.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {contract.signed_count}/{contract.signers_count} signed
                    </span>
                  </TableCell>
                  <TableCell>{formatCurrency(contract.value, contract.currency)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(contract.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.contract_id}`)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </DropdownMenuItem>
                        {canAddEdit('CONTRACTS') && contract.status === 'DRAFT' && (
                          <>
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.contract_id}/edit`)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.contract_id}/send`)}>
                              <Send className="mr-2 h-4 w-4" />
                              Send for Signing
                            </DropdownMenuItem>
                          </>
                        )}
                        {canAddEdit('CONTRACTS') && <DropdownMenuSeparator />}
                        {canAddEdit('CONTRACTS') && contract.status !== 'SIGNED' && contract.status !== 'CANCELLED' && (
                          <DropdownMenuItem onClick={() => cancelMutation.mutate(contract.contract_id)}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        )}
                        {canAddEdit('CONTRACTS') && contract.status === 'DRAFT' && (
                          <DropdownMenuItem
                            onClick={() => setDeleteId(contract.contract_id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contract</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contract? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

